<template>
  <b-modal 
    id="modal-upload-certificate" 
    centered 
    hide-footer 
    title="Bulk Upload Sertifikat"
    size="lg"
    :no-close-on-backdrop="isUploading">
    <div class="p-4">
      <!-- Course Info -->
      <div class="course-info-section mb-4 p-3 rounded" style="background-color: #fafafa;">
        <div class="d-flex align-items-center">
          <div class="course-icon me-3">
            <i class="fe fe-book-open text-danger" style="font-size: 1.5rem;"></i>
          </div>
          <div>
            <h6 class="mb-1 font-weight-bold">{{ selectedCourse?.title || '-' }}</h6>
            <small class="text-muted">
              <i class="fe fe-briefcase me-1"></i>
              {{ selectedCourse?.provider_name || '-' }}
            </small>
          </div>
        </div>
      </div>

      <!-- Important Notice -->
      <div class="alert alert-warning border-warning mb-4" role="alert">
        <div class="d-flex">
          <div class="me-3">
            <i class="fe fe-alert-circle" style="font-size: 1.5rem;"></i>
          </div>
          <div>
            <h6 class="alert-heading mb-2">Ketentuan Nama File</h6>
            <ul class="mb-0 small" style="padding-left: 1.2rem;">
              <li class="mb-1">File sertifikat di dalam ZIP harus menggunakan nama <strong>NIK</strong> peserta (contoh: <code>C-12345.pdf</code>)</li>
              <li class="mb-1">Format file yang diterima: <strong>JPG</strong>, <strong>PNG</strong>, atau <strong>PDF</strong></li>
              <li>Ukuran ZIP maksimal: <strong>10MB</strong></li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section mb-4">
        <label class="form-label font-weight-bold">
          File ZIP Sertifikat <span class="text-danger">*</span>
        </label>
        <p class="text-muted small mb-2">
          Upload file ZIP yang berisi beberapa sertifikat.
        </p>
        
        <!-- Dropzone -->
        <div 
          class="dropzone p-4 border rounded text-center"
          :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop">
          
          <!-- File Belum Dipilih -->
          <div v-if="!selectedFile">
            <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem;"></i>
            <p class="text-muted mb-2">Tarik file ZIP ke sini atau</p>
            <label class="btn btn-outline-danger btn-sm">
              Pilih File
              <input 
                type="file" 
                accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
                @change="handleFileSelect"
                hidden>
            </label>
          </div>

          <!-- File Selected -->
          <div v-else>
            <div class="zip-preview mb-3">
              <div class="zip-icon-container">
                <i class="fe fe-archive"></i>
              </div>
              <p class="zip-label mb-0">File ZIP</p>
            </div>
            <div class="d-flex align-items-center justify-content-center gap-2">
              <span class="text-success">
                <i class="fe fe-check-circle me-1"></i>
                {{ selectedFile?.name }}
              </span>
              <button 
                class="btn btn-outline-danger btn-sm"
                @click="removeFile"
                :disabled="isUploading">
                <i class="fe fe-x"></i>
              </button>
            </div>
            <p class="text-muted small mt-2 mb-0">
              Ukuran: {{ formatFileSize(selectedFile?.size) }}
            </p>
          </div>
        </div>
        <small v-if="!selectedFile && submitted" class="text-danger">
          Pilih file sertifikat terlebih dahulu.
        </small>
      </div>

      <!-- Note Section -->
      <div class="note-section mb-4">
        <label class="form-label font-weight-bold">
          Catatan <span class="text-muted">(Opsional)</span>
        </label>
        <textarea 
          v-model="note"
          class="form-control"
          rows="3"
          placeholder="Tambahkan catatan terkait sertifikat..."
          maxlength="500">
        </textarea>
        <small class="text-muted">{{ note.length }}/500 karakter</small>
      </div>

      <!-- Results Table -->
      <div v-if="uploadResults.length > 0" class="results-section mb-4">
        <label class="form-label font-weight-bold text-danger">
          <i class="fe fe-alert-triangle me-1"></i>
          Hasil Upload ({{ uploadResults.length }} file error)
        </label>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="table-light">
              <tr>
                <th style="width: 50px;">#</th>
                <th>Nama File</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(result, index) in uploadResults" :key="index">
                <td class="text-center">{{ index + 1 }}</td>
                <td>{{ result.file || '-' }}</td>
                <td class="text-danger">{{ result.message || result.error || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="modal-footer border-top">
      <button 
        class="btn btn-secondary"
        @click="closeModal"
        :disabled="isUploading">
        Batal
      </button>
      <button 
        class="btn btn-danger"
        @click="submitUpload"
        :disabled="isUploading">
        <span v-if="isUploading" class="spinner-border spinner-border-sm me-1" role="status"></span>
        {{ isUploading ? 'Mengupload...' : 'Upload Sertifikat' }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import courseCompletionMitraApi from '@/api/course-completion-mitra/course-completion-mitra';
import { normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalUploadCertificate',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      note: '',
      isDragging: false,
      isUploading: false,
      submitted: false,
      uploadResults: []
    }
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    processFile(file) {
      if (!file) return;

      // Validate file type
      const allowedTypes = ['application/zip', 'application/x-zip', 'application/x-zip-compressed'];
      const fileName = file.name.toLowerCase();
      
      if (!allowedTypes.includes(file.type) && !fileName.endsWith('.zip')) {
        this.$swal({
          title: 'Tipe File Tidak Valid',
          text: 'Upload file dengan format .zip',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: 'File Terlalu Besar',
          text: 'Ukuran file maksimal 10 MB',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      this.selectedFile = file;
      this.uploadResults = []; // Clear previous results
    },

    removeFile() {
      this.selectedFile = null;
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async submitUpload() {
      this.submitted = true;

      if (!this.selectedFile) {
        return;
      }

      this.isUploading = true;
      this.uploadResults = [];

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.note) {
          formData.append('note', this.note);
        }

        const response = await courseCompletionMitraApi.bulkUploadCompletion(
          this.selectedCourse.id,
          formData
        );

        const result = response.data?.data || {};
        const errors = Array.isArray(result) ? result : (result.errors || []);
        const submitted = Array.isArray(result?.submitted) ? result.submitted : [];

        if (errors.length > 0) {
          this.uploadResults = errors;
          
          this.$swal({
            title: 'Upload Selesai dengan Error',
            text: `${this.uploadResults.length} file mengalami error. Cek tabel hasil upload di bawah untuk memperbaiki file.`,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#f6c343'
          });
        } else {
          this.$swal({
            title: 'Upload Berhasil',
            text: `${submitted.length || 'Semua'} sertifikat berhasil diupload.`,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#00d97e'
          }).then(() => {
            this.$emit('certificate-uploaded');
            this.closeModal();
          });
        }
      } catch (error) {
        console.error('Error uploading certificates:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.response?.data?.message || error.message || 'File belum berhasil diunggah'),
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isUploading = false;
      }
    },

    closeModal() {
      this.resetForm();
      this.$bvModal.hide('modal-upload-certificate');
    },

    resetForm() {
      this.removeFile();
      this.note = '';
      this.submitted = false;
      this.uploadResults = [];
    }
  }
}
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* ZIP Preview Styles */
.zip-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.zip-preview .zip-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.zip-preview .zip-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.zip-preview .zip-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}

/* Results Table Styles */
.results-section {
  max-height: 300px;
  overflow-y: auto;
}

.results-section .table {
  font-size: 0.875rem;
  margin-bottom: 0;
}

.results-section .table th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
}
</style>
